/**
 * SidebarCategoryReorder — Draggable category reorder UI for the Software section.
 *
 * Extracted from SidebarNav to keep the main sidebar component within the 700-line target.
 */
import { useCallback, useRef, useState } from "react";
import { softwareCategoryMeta, type NavGroup } from "../../nav/navConfig";
import { cn } from "../../utils/cn";
import { trackEvent } from "../../lib/auditCollector";
import { toast } from "../../lib/toast";
import { Icon } from "../ui/Icon";

type SidebarCategoryReorderProps = {
  groups: NavGroup[];
  categoryIds: string[];
  userCategoryOrder: string[] | null;
  prefsSaving: boolean;
  updatePrefs: (prefs: { softwareQuickNavCategoryOrder: string[] | null }) => Promise<{ ok: boolean; error?: string }>;
  onExitReorder: () => void;
};

export const SidebarCategoryReorder = ({
  groups,
  categoryIds,
  userCategoryOrder,
  prefsSaving,
  updatePrefs,
  onExitReorder,
}: SidebarCategoryReorderProps) => {
  const dragSourceIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleDragStart = useCallback((index: number) => {
    dragSourceIdx.current = index;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(index);
  }, []);

  const handleDrop = useCallback(
    async (targetIndex: number) => {
      const sourceIndex = dragSourceIdx.current;
      dragSourceIdx.current = null;
      setDragOverIdx(null);

      if (sourceIndex === null || sourceIndex === targetIndex) return;
      if (!categoryIds.length) return;

      const reordered = [...categoryIds];
      const [moved] = reordered.splice(sourceIndex, 1);
      reordered.splice(targetIndex, 0, moved);

      const result = await updatePrefs({ softwareQuickNavCategoryOrder: reordered });

      if (result.ok) {
        toast.success("Category order saved");
        trackEvent({
          category: "ui",
          action: "sidebar.category-reorder",
          component: "SidebarNav",
          details: { order: reordered },
        });
      } else {
        toast.error(result.error ?? "Failed to save category order");
      }
    },
    [categoryIds, updatePrefs]
  );

  const handleDragEnd = useCallback(() => {
    dragSourceIdx.current = null;
    setDragOverIdx(null);
  }, []);

  const handleReset = useCallback(async () => {
    const result = await updatePrefs({ softwareQuickNavCategoryOrder: null });

    if (result.ok) {
      toast.success("Category order reset to default");
      onExitReorder();
      trackEvent({
        category: "ui",
        action: "sidebar.category-reorder-reset",
        component: "SidebarNav",
      });
    } else {
      toast.error(result.error ?? "Failed to reset category order");
    }
  }, [updatePrefs, onExitReorder]);

  return (
    <div className="space-y-2">
      <p className="px-1 text-xs text-muted-foreground">Drag categories to reorder</p>
      <ul className="space-y-1" role="list">
        {groups.map((group, idx) => {
          const meta = softwareCategoryMeta[group.id];
          return (
            <li
              key={group.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", group.id);
                handleDragStart(idx);
              }}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(idx);
              }}
              className={cn(
                "flex cursor-grab items-center gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors select-none active:cursor-grabbing",
                dragOverIdx === idx && dragSourceIdx.current !== idx
                  ? "border-primary/50 bg-primary/5"
                  : "border-border/60 bg-muted/30 hover:bg-muted/50"
              )}
              aria-roledescription="draggable category"
              aria-label={`${group.label} — position ${idx + 1} of ${groups.length}`}
            >
              <Icon
                icon="lucide:grip-vertical"
                className="h-4 w-4 flex-shrink-0 text-muted-foreground/60"
                aria-hidden
              />
              <Icon
                icon={meta?.icon ?? group.icon ?? "lucide:folder"}
                className="h-4 w-4 flex-shrink-0"
              />
              <span className="truncate">{group.label}</span>
              <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                {group.items.length}
              </span>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        onClick={handleReset}
        disabled={prefsSaving || userCategoryOrder === null}
        className={cn(
          "mt-2 w-full rounded-md border border-border/60 px-3 py-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
          userCategoryOrder === null
            ? "cursor-not-allowed text-muted-foreground/40"
            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        )}
      >
        Reset to default order
      </button>
    </div>
  );
};
